import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { environment } from 'src/environments/environment';

const REFRESH_MARGIN_MS = 20 * 1000;

type TokenResponse = {
  access_token?: unknown;
  expires_in?: unknown;
  token_type?: unknown;
};

@Injectable({
  providedIn: 'root',
})
export class BigQueryAuthService {
  private readonly http = inject(HttpClient);
  private readonly tokenServiceUrl = environment.BQ_ENDPOINT_LOGGER_TOKEN_URL;
  private token: string | null = null;
  private tokenExpiresAt = 0;
  private pendingToken: Promise<string> | null = null;

  getToken(): Promise<string> {
    const endpointId = window.location.hostname?.trim();
    if (!endpointId) {
      return Promise.reject();
    }

    if (this.token && Date.now() < this.tokenExpiresAt) {
      return Promise.resolve(this.token);
    }

    if (this.pendingToken) {
      return this.pendingToken;
    }

    this.pendingToken = this.fetchAccessToken(endpointId).finally(() => {
      this.pendingToken = null;
    });
    return this.pendingToken;
  }

  private async fetchAccessToken(endpointId: string): Promise<string> {
    const response = await firstValueFrom(
      this.http.get<TokenResponse>(this.tokenServiceUrl, {
        params: {
          endpointId,
          endpointType: 'InApp',
        },
      }),
    );

    const token =
      typeof response?.access_token === 'string'
        ? response.access_token.trim()
        : '';

    if (
      !token ||
      typeof response.expires_in !== 'number' ||
      response.expires_in <= 0
    ) {
      this.token = null;
      this.tokenExpiresAt = 0;
      return Promise.reject();
    }

    this.token = token;
    this.tokenExpiresAt =
      Date.now() + response.expires_in * 1000 - REFRESH_MARGIN_MS;
    return this.token;
  }
}
